import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import Toggle from 'react-toggle';
import intlShape from '@/shared/utils/intlShape';
import classnames from 'classnames';

import UserSearch from '@/shared/components/input/UserSearch';
import { CapacityTaskSelect } from '@/shared/components/input/filterInputs/FilterCapacityTaskSelect';
import EmployeeTagMultiSelect from '@/modules/hrm/EmployeeTagMultiSelect';
import EnumMultiSelect from '@/shared/components/input/multiselect/EnumMultiSelect';
import TeamMultiSelect from '@/shared/components/input/multiselect/TeamMultiSelect';
import CompanyMultiSelect from '@/shared/components/input/multiselect/CompanyMultiSelect';
import LegalEntityMultiSelect from '@/shared/components/input/multiselect/LegalEntityMultiSelect';
import { hasMultipleCompanies } from '@/shared/components/security/HasMultiCompany';
import HasMultipleLegalEntities from '@/shared/components/security/HasMultipleLegalEntities';
import SavedFiltersButton from '@/shared/components/SavedFiltersButton';

import messages from '../hrmMessages';

import DelayedDateRangePicker from '@/shared/components/input/DelayedDateRangePicker';
import PeriodButtonGroup, { getPeriodStartEndDates } from '@/shared/components/PeriodButtonGroup';
import moment from 'moment';
import { isBeforeMinimum } from '@/shared/utils/validationHelpers';
import ToastrService from '@/shared/services/ToastrService';

const periods = [
  'week',
  'nextWeek',
  'month',
  'nextMonth',
  'quarter-1',
  'quarter',
  'nextQuarter',
  'year-1',
  'year',
  'year+1',
];

export const defaultFilters = {
  deadlineFrom: moment.utc().startOf('year'),
  deadlineUntil: moment
    .utc()
    .endOf('year')
    .startOf('day'),
  period: 'year',
  mainTaskTemplateId: undefined,
  displayMode: 0,
  groupBy: 0,
  useExecutionDate: false,
  withEmployeeLabels: [],
  ownCompanies: [],
  legalEntities: [],
  profileId: null,
  teams: [],
  employeeTypes: [],
};

const maxDate = moment
  .utc()
  .add(1, 'year')
  .endOf('year');
const defaultDeadlineFrom = () => moment.utc().startOf('year');
const defaultDeadlineUntil = () =>
  moment
    .utc()
    .endOf('year')
    .startOf('day');
const isOutsideRange = date => isBeforeMinimum(date) || moment(date).isAfter(maxDate, 'day');

class CapacityReportFilters extends Component {
  isAtLeastOneMonth = (from, until) => {
    if (!from || !until) return false;
    return moment(until).diff(moment(from), 'months') >= 1;
  };

  isAtLeastOneQuarter = (from, until) => {
    if (!from || !until) return false;
    return moment(until).diff(moment(from), 'months') >= 3;
  };

  filterChanged = filter => {
    const { filters, onFilterChange } = this.props;
    const newFilters = { ...filters, ...filter };

    if (moment.isMoment(newFilters.deadlineFrom)) {
      newFilters.deadlineFrom = moment.utc(newFilters.deadlineFrom.format('YYYY-MM-DD'));
    }
    if (moment.isMoment(newFilters.deadlineUntil)) {
      newFilters.deadlineUntil = moment.utc(newFilters.deadlineUntil.format('YYYY-MM-DD'));
    }

    const groupBy = newFilters.groupBy ?? 0;
    if (
      groupBy === 2 &&
      !this.isAtLeastOneQuarter(newFilters.deadlineFrom, newFilters.deadlineUntil)
    ) {
      newFilters.groupBy = 0;
    } else if (
      groupBy === 1 &&
      !this.isAtLeastOneMonth(newFilters.deadlineFrom, newFilters.deadlineUntil)
    ) {
      newFilters.groupBy = 0;
    }

    if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
      onFilterChange(newFilters);
    }
  };

  dateRangeChanged = (startDate, endDate) => {
    const { intl, filters } = this.props;

    if (!startDate && !endDate) {
      this.filterChanged({
        period: 'year',
        deadlineFrom: defaultDeadlineFrom(),
        deadlineUntil: defaultDeadlineUntil(),
      });
      return;
    }

    const maxStr = maxDate.format('YYYY-MM-DD');
    const startStr = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
    const endStr = endDate ? moment(endDate).format('YYYY-MM-DD') : null;

    if (!startStr || !endStr || startStr > maxStr || endStr > maxStr) {
      const [periodFrom, periodUntil] = getPeriodStartEndDates(filters.period || 'year');
      ToastrService.error(intl.formatMessage(messages.CapacityMaxDateWarning));
      this.filterChanged({
        period: filters.period || 'year',
        deadlineFrom: periodFrom || defaultDeadlineFrom(),
        deadlineUntil: periodUntil || defaultDeadlineUntil(),
      });
      return;
    }

    this.filterChanged({ deadlineFrom: startDate, deadlineUntil: endDate });
  };

  render() {
    const {
      filters,
      columns,
      intl,
      availableMainTaskTemplateIds,
      employeeProfiles,
      onFilterChange,
    } = this.props;

    return (
      <div className="info-block container-fluid">
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <DelayedDateRangePicker
                startDate={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                startDateId="CapacityReportFilterDeadlineFrom"
                endDate={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                endDateId="CapacityReportFilterDeadlineUntil"
                onDatesChange={({ startDate, endDate }) =>
                  this.dateRangeChanged(startDate, endDate)
                }
                intl={intl}
                startDatePlaceholderText={intl.formatMessage(messages.DeadlineFrom)}
                endDatePlaceholderText={intl.formatMessage(messages.DeadlineUntil)}
                isOutsideRange={isOutsideRange}
                showClearDates={false}
                minimumNights={6}
                block
              />
            </div>
          </div>
          <div className="col-lg-9">
            <div className="form-group">
              <PeriodButtonGroup
                dateFrom={filters.deadlineFrom ? moment(filters.deadlineFrom) : undefined}
                dateUntil={filters.deadlineUntil ? moment(filters.deadlineUntil) : undefined}
                period={filters.period}
                onChange={({ dateFrom, dateUntil, period }) =>
                  this.filterChanged({ deadlineFrom: dateFrom, deadlineUntil: dateUntil, period })
                }
                visiblePeriods={periods}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <CapacityTaskSelect
                initialValue={filters.mainTaskTemplateId}
                onChange={value =>
                  this.filterChanged({
                    mainTaskTemplateId: value || undefined,
                  })
                }
                availableTemplateIds={availableMainTaskTemplateIds}
                placeholder={intl.formatMessage(messages.AllMainTasks)}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <EmployeeTagMultiSelect
                value={filters.withEmployeeLabels || []}
                onChange={val => this.filterChanged({ withEmployeeLabels: val })}
                placeholder={intl.formatMessage(messages.EmployeeLabels)}
              />
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <select
                className="form-control"
                value={filters.profileId || ''}
                onChange={e => this.filterChanged({ profileId: e.target.value || null })}
              >
                <option value="">{intl.formatMessage(messages.AllProfiles)}</option>
                {(employeeProfiles || []).map(profile => (
                  <option key={profile.id} value={profile.id}>
                    {profile.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <TeamMultiSelect
                selection={filters.teams || []}
                onChange={val => this.filterChanged({ teams: val })}
              />
            </div>
          </div>
        </div>
        <div className="row">
          {hasMultipleCompanies() && (
            <>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <CompanyMultiSelect
                    selection={filters.ownCompanies || []}
                    onChange={val => this.filterChanged({ ownCompanies: val })}
                  />
                </div>
              </div>
              <HasMultipleLegalEntities>
                <div className="col-lg-3 col-md-6">
                  <div className="form-group">
                    <LegalEntityMultiSelect
                      selection={filters.legalEntities || []}
                      onChange={val => this.filterChanged({ legalEntities: val })}
                    />
                  </div>
                </div>
              </HasMultipleLegalEntities>
            </>
          )}
          {!hasMultipleCompanies() && (
            <HasMultipleLegalEntities>
              <div className="col-lg-3 col-md-6">
                <div className="form-group">
                  <LegalEntityMultiSelect
                    selection={filters.legalEntities || []}
                    onChange={val => this.filterChanged({ legalEntities: val })}
                  />
                </div>
              </div>
            </HasMultipleLegalEntities>
          )}
          <div className="col-lg-3 col-md-6">
            <div className="form-group">
              <EnumMultiSelect
                type="employeeType"
                selection={filters.employeeTypes}
                orderByLabel
                onChange={value => this.filterChanged({ employeeTypes: value })}
                placeholder={intl.formatMessage(messages.EmployeeType)}
              />
            </div>
          </div>
        </div>
        <div className="capacity-report-options-row">
          <SavedFiltersButton
            screen="CapacityReport"
            currentFilters={filters}
            defaultFilters={this.props.defaultFilters || defaultFilters}
            onFiltersChanged={onFilterChange}
            ignoreFilters={['period']}
          />
          <div className="capacity-report-toggle-row">
            <div className="form-group">
              <label className="react-toggle-wrapper">
                <Toggle
                  checked={!!filters.useExecutionDate}
                  onChange={e => this.filterChanged({ useExecutionDate: e.target.checked })}
                />
                <span className="label-text">
                  <FormattedMessage {...messages.CapacityUseExecutionDate} />
                </span>
              </label>
            </div>
            <div className="form-group">
              <label className="margin5R">
                <FormattedMessage {...messages.CapacityDisplayMode} />:
              </label>
              <div role="group" className="btn-group">
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 0 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeDuration)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 0,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeDuration)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 1 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeTaskCount)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 1,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeTaskCount)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ displayMode: 2 })}
                  title={intl.formatMessage(messages.CapacityDisplayModeRelationCount)}
                  className={classnames('btn btn-default', {
                    active: (filters.displayMode ?? 0) === 2,
                  })}
                >
                  {intl.formatMessage(messages.CapacityDisplayModeRelationCount)}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label className="margin5R">
                <FormattedMessage {...messages.CapacityGroupByPeriod} />:
              </label>
              <div role="group" className="btn-group">
                <button
                  type="button"
                  onClick={() => this.filterChanged({ groupBy: 0 })}
                  title={intl.formatMessage(messages.CapacityGroupByPeriodNone)}
                  className={classnames('btn btn-default', {
                    active: (filters.groupBy ?? 0) === 0,
                  })}
                >
                  {intl.formatMessage(messages.CapacityGroupByPeriodNone)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ groupBy: 1 })}
                  title={intl.formatMessage(messages.CapacityGroupByPeriodMonth)}
                  disabled={!this.isAtLeastOneMonth(filters.deadlineFrom, filters.deadlineUntil)}
                  className={classnames('btn btn-default', {
                    active: (filters.groupBy ?? 0) === 1,
                  })}
                >
                  {intl.formatMessage(messages.CapacityGroupByPeriodMonth)}
                </button>
                <button
                  type="button"
                  onClick={() => this.filterChanged({ groupBy: 2 })}
                  title={intl.formatMessage(messages.CapacityGroupByPeriodQuarter)}
                  disabled={!this.isAtLeastOneQuarter(filters.deadlineFrom, filters.deadlineUntil)}
                  className={classnames('btn btn-default', {
                    active: (filters.groupBy ?? 0) === 2,
                  })}
                >
                  {intl.formatMessage(messages.CapacityGroupByPeriodQuarter)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

CapacityReportFilters.propTypes = {
  intl: intlShape.isRequired,
  filters: PropTypes.shape({
    deadlineFrom: PropTypes.string,
    deadlineUntil: PropTypes.string,
    period: PropTypes.string,
    mainTaskTemplateId: PropTypes.string,
    displayMode: PropTypes.number,
    groupBy: PropTypes.number,
    useExecutionDate: PropTypes.bool,
    withEmployeeLabels: PropTypes.arrayOf(PropTypes.string),
    ownCompanies: PropTypes.arrayOf(PropTypes.string),
    profileId: PropTypes.string,
    teams: PropTypes.arrayOf(PropTypes.string),
    employeeTypes: PropTypes.arrayOf(PropTypes.string),
  }),
  onFilterChange: PropTypes.func.isRequired,
  availableMainTaskTemplateIds: PropTypes.arrayOf(PropTypes.string),
  employeeProfiles: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
  ),
};

CapacityReportFilters.defaultProps = {
  filters: { ...defaultFilters },
  employeeProfiles: [],
};
export default injectIntl(CapacityReportFilters);
